import type {
  BatchArgs,
  BatchResponse,
  DemoOverlay,
  Entity,
  Folder,
  IDemo,
  IPresentation,
  IRecording,
  Playbook,
  Sort,
  SuggestionsSearchResponse,
  Tour,
} from '@carboncopyco/types';
import type { RecordingTypes } from '@carboncopyco/types';
import { Debug, removeUndefinedValues } from '@carboncopyco/utils';
import { flattenSearchResults, handleBatchRequest } from '../utils';
import { mainApi } from './mainApi';

Debug.extend('demostack-data:api:searchApi');

export interface GetSuggestionsArgs {
  organizationId: string;
  query: string;
  entities?: Entity[];
  mineOnly?: boolean;
  sort?: string;
  limit?: number;
  deletedEntities?: boolean;
  workspaces?: string;
  folderIds?: Folder['id'][];
}

export interface SearchEndpointArgs extends BatchArgs<Sort> {
  organizationId: string;
  entities?: Entity[];
  creators?: string[];
  query: string;
  deletedEntities?: boolean | 'include_all';
  allOrgWorkspaces?: boolean;
  workspaces?: string;
  recordingType?: RecordingTypes[];
  folderIds?: Folder['id'][];
  includeShared?: boolean;
}

export interface RawSearchResult {
  entityType: Entity;
  item: IDemo | IPresentation | IRecording | Tour;
}

export type SearchResult = {
  entityType: Entity;
  readOnly?: boolean;
} & (IDemo | DemoOverlay | IRecording | IPresentation | Tour | Playbook);

export const isTourType = (item: Record<string, unknown>): item is Tour =>
  item.entityType === 'tour';

export const isDemoType = (item: Record<string, unknown>): item is IDemo =>
  item.entityType === 'demo';

export const isOverlayType = (
  item: Record<string, unknown>
): item is DemoOverlay => item.entityType === 'overlay';

export const isCloneType = (
  item: Record<string, unknown>
): item is IRecording => item.entityType === 'record';

export const isPlaybookType = (
  item: Record<string, unknown>
): item is Playbook => item.entityType === 'playbook';

const searchApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    getSuggestions: builder.query<
      SuggestionsSearchResponse,
      GetSuggestionsArgs
    >({
      query: ({
        organizationId,
        entities,
        sort,
        query,
        mineOnly,
        limit,
        deletedEntities,
        workspaces = 'all',
        folderIds,
      }) => {
        return {
          url: `/api/${organizationId}/suggestions`,
          method: 'GET',
          params: {
            ...removeUndefinedValues({
              includeShared: true,
              entities: entities?.join(','),
              workspaces,
              sort,
              mineOnly,
              limit,
              deletedEntities,
              folderIds: folderIds?.join(','),
            }),
            query,
          },
        };
      },
      transformResponse: (response) => ({
        ...response,
        results: response.results.map((result) =>
          result.recordingType === 'live_demo'
            ? { ...result, entityType: 'overlay' }
            : result
        ),
      }),
    }),

    getSearchResults: builder.query<
      BatchResponse<SearchResult>,
      SearchEndpointArgs
    >({
      query: ({
        organizationId,
        query,
        entities,
        creators,
        workspaces = 'all',
        recordingType,
        folderIds,
        includeShared = true,
        deletedEntities,
        allOrgWorkspaces,
        ...rest
      }) => {
        return handleBatchRequest({
          ...rest,
          params: removeUndefinedValues(
            {
              allOrgWorkspaces,
              deletedEntities,
              includeShared,
              workspaces,
              query,
              entities: entities?.join(','),
              creators: creators?.join(','),
              recordingType: recordingType?.join(','),
              folderIds: folderIds?.join(','),
            },
            { keepEmptyStrings: true }
          ),
          url: `/api/${organizationId}/search`,
        });
      },
      transformResponse: flattenSearchResults,
    }),
  }),
});

export const { useLazyGetSuggestionsQuery, useLazyGetSearchResultsQuery } =
  searchApi;
